import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import { CurriculumLesson } from './data/peninsula-district';

// A faithful, non-interactive facsimile of the real Pressto student-preview lesson
// (matched to the live /document student-preview UI), rendered from a lesson's real
// data so a curriculum director can SEE the embedded scaffolds, not just the prompt.

const PURPLE = '#5C4FB8';
const INK = '#1e1e1e';
const SHADOW = '4px 4px 0px #1e1e1e';
const SHADOW_SM = '2px 2px 0px #1e1e1e';

// Scaffold structure per writing plan — blocks, sentence starters, and signal words
// sourced directly from write-app/src/configuration/writing-plans.ts + starters.ts.
const PLAN_STRUCTURE: Record<string, {
  label: string;
  blocks: { icon: string; label: string }[];
  starters: string[];
  signalWords: string[];
}> = {
  'op-ed': {
    label: 'Opinion',
    blocks: [
      { icon: 'T', label: 'Title' },
      { icon: '◈', label: 'Introduction' },
      { icon: '◆', label: 'Claim or Opinion' },
      { icon: '≡', label: 'Evidence' },
      { icon: '✷', label: 'Conclusion' },
    ],
    starters: [
      'I believe that…',
      'One reason is…',
      'Another reason is…',
      'In conclusion, I think…',
    ],
    signalWords: ['opinion', 'believe', 'think', 'agree', 'disagree', 'reason', 'evidence', 'for example', 'in addition to', 'finally', 'significant', 'important'],
  },
  description: {
    label: 'Description',
    blocks: [
      { icon: 'T', label: 'Title' },
      { icon: '✦', label: 'Main Idea' },
      { icon: '≣', label: 'Supporting Details' },
      { icon: '✷', label: 'Conclusion' },
    ],
    starters: [
      'The main idea of this text is…',
      'One important detail from the text is…',
      'In conclusion, the text teaches us that…',
    ],
    signalWords: ['for instance', 'for example', 'furthermore', 'to illustrate', 'specifically', 'such as', 'moreover', 'in addition'],
  },
  sequence: {
    label: 'Sequence',
    blocks: [
      { icon: 'T', label: 'Title' },
      { icon: '◈', label: 'Introduction' },
      { icon: '▶', label: 'Event / Step' },
      { icon: '✷', label: 'Conclusion' },
    ],
    starters: [
      'This explains how…',
      'First,…',
      'Next,…',
      'Finally,…',
    ],
    signalWords: ['first', 'second', 'third', 'next', 'then', 'after', 'before', 'finally', 'later', 'when', 'meanwhile', 'previously'],
  },
  'cause-effect': {
    label: 'Cause and Effect',
    blocks: [
      { icon: 'T', label: 'Title' },
      { icon: '◈', label: 'Introduction' },
      { icon: '→', label: 'Cause' },
      { icon: '←', label: 'Effect' },
      { icon: '✷', label: 'Conclusion' },
    ],
    starters: [
      'This happened because…',
      'One cause is…',
      'As a result,…',
      'In conclusion,…',
    ],
    signalWords: ['because', 'as a result', 'therefore', 'consequently', 'due to', 'since', 'hence', 'thus', 'for this reason', 'in order to'],
  },
  'compare-contrast': {
    label: 'Compare and Contrast',
    blocks: [
      { icon: 'T', label: 'Title' },
      { icon: '◈', label: 'Introduction' },
      { icon: '⊜', label: 'Compare' },
      { icon: '≠', label: 'Contrast' },
      { icon: '✷', label: 'Conclusion' },
    ],
    starters: [
      'Both… and…',
      'Similarly,…',
      'However,…',
      'In contrast,…',
    ],
    signalWords: ['similarly', 'both', 'in comparison', 'however', 'by contrast', 'on the other hand', 'yet', 'but', 'despite', 'as opposed to'],
  },
  'problem-solution': {
    label: 'Problem and Solution',
    blocks: [
      { icon: 'T', label: 'Title' },
      { icon: '◈', label: 'Introduction' },
      { icon: '⚡', label: 'Problem' },
      { icon: '✓', label: 'Solution' },
      { icon: '✷', label: 'Conclusion' },
    ],
    starters: [
      'The problem is…',
      'This is difficult because…',
      'One solution is…',
      'This would help because…',
    ],
    signalWords: ['problem', 'solution', 'because', 'cause', 'as a result', 'in order to', 'to solve', 'to fix', 'a difficulty is', 'therefore'],
  },
  'retell-the-narrative': {
    label: 'Retell the Narrative',
    blocks: [
      { icon: 'T', label: 'Title' },
      { icon: '◈', label: 'Introduction' },
      { icon: '≣', label: 'Body' },
      { icon: '✷', label: 'Closing' },
    ],
    starters: [
      'This story is about…',
      'In the beginning,…',
      'Later,…',
      'At the end,…',
    ],
    signalWords: ['first', 'next', 'then', 'after', 'later', 'meanwhile', 'suddenly', 'following', 'at the end', 'last'],
  },
  'personal-narrative': {
    label: 'Personal Narrative',
    blocks: [
      { icon: 'T', label: 'Title' },
      { icon: '◈', label: 'Introduction' },
      { icon: '👥', label: 'People and Setting' },
      { icon: '≡', label: 'Details' },
      { icon: '✷', label: 'Conclusion' },
    ],
    starters: [
      'I remember when…',
      'This happened at…',
      'I felt…',
      'Looking back,…',
    ],
    signalWords: ['at first', 'before', 'during', 'while', 'next', 'after', 'later', 'suddenly', 'as soon as', 'immediately', 'meanwhile'],
  },
  story: {
    label: 'Story',
    blocks: [
      { icon: 'T', label: 'Title' },
      { icon: '🏠', label: 'Setting and Characters' },
      { icon: '⚡', label: 'Problem or Goal' },
      { icon: '→', label: 'Attempts' },
      { icon: '✓', label: 'Solution' },
      { icon: '✷', label: 'Resolution' },
    ],
    starters: [
      'Once upon a time…',
      'The problem was…',
      'They tried to…',
      'In the end,…',
    ],
    signalWords: ['at first', 'before', 'during', 'while', 'next', 'later', 'after', 'suddenly', 'then', 'finally', 'meanwhile'],
  },
};

function SpeakerIcon() {
  return (
    <Box component="span" sx={{ color: PURPLE, fontSize: '1rem', lineHeight: 1 }} aria-label="Read aloud">🔊</Box>
  );
}

function WordChip({ label, bg, color }: { label: string; bg: string; color: string }) {
  return (
    <Box sx={{
      backgroundColor: bg, color, borderRadius: '4px', px: 1, py: 0.4,
      fontFamily: 'Lato, sans-serif', fontWeight: 600, fontSize: '0.78rem', whiteSpace: 'nowrap',
    }}>{label}</Box>
  );
}

function ScaffoldPanel({ icon, title, children, defaultOpen = false }: { icon: string; title: string; children?: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box sx={{ backgroundColor: '#fff', border: `2px solid ${INK}`, borderRadius: '8px', boxShadow: SHADOW_SM, p: 1.5 }}>
      <Box onClick={() => setOpen((o) => !o)} sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none' }}>
        <Box component="span" sx={{ fontSize: '1rem' }}>{icon}</Box>
        <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '0.85rem', color: INK, flex: 1 }}>{title}</Typography>
        <Box component="span" sx={{ color: '#999', fontWeight: 700, transition: 'transform 120ms', transform: open ? 'rotate(90deg)' : 'none' }}>›</Box>
      </Box>
      {open && children && <Box sx={{ mt: 1.25 }}>{children}</Box>}
    </Box>
  );
}

function CTAButton({ children, onClick, dark = true }: { children: React.ReactNode; onClick: () => void; dark?: boolean }) {
  return (
    <MuiButton
      variant="contained"
      onClick={onClick}
      sx={{
        backgroundColor: dark ? '#2C2C2C' : '#FFE082', color: dark ? '#fff' : '#2C2C2C',
        fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.95rem', textTransform: 'none',
        borderRadius: '6px', border: `2px solid ${INK}`, boxShadow: SHADOW, px: 3.5, py: 1.25,
        '&:hover': { boxShadow: SHADOW_SM, transform: 'translate(2px,2px)', backgroundColor: dark ? '#2C2C2C' : '#FFE082' },
        transition: 'all 100ms',
      }}
    >{children}</MuiButton>
  );
}

export default function PressoLessonPreview({ lesson, onClose, onRegister, onPricing }: {
  lesson: CurriculumLesson; onClose: () => void; onRegister: () => void; onPricing: () => void;
}) {
  const plan = PLAN_STRUCTURE[lesson.plan] || PLAN_STRUCTURE.description;

  return (
    <Box
      onClick={onClose}
      sx={{
        position: 'fixed', inset: 0, zIndex: 1300, backgroundColor: 'rgba(30,30,30,0.55)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', p: { xs: 1.5, md: 4 },
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          backgroundColor: '#F7F8FA', border: `2px solid ${INK}`, borderRadius: '12px', boxShadow: SHADOW,
          width: '100%', maxWidth: 1080, my: 'auto', overflow: 'hidden',
        }}
      >
        {/* App-style top bar */}
        <Box sx={{ backgroundColor: '#fff', borderBottom: `2px solid ${INK}`, px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 900, color: PURPLE, fontSize: '1.1rem', letterSpacing: '-0.02em' }}>PRESSTO</Typography>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '0.95rem', color: INK, lineHeight: 1.1 }}>{lesson.title}</Typography>
            <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.72rem', color: '#7AA37A' }}>Saved ✓</Typography>
          </Box>
          <Box sx={{ backgroundColor: '#EDE9F7', color: PURPLE, borderRadius: '4px', px: 1, py: 0.3, fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {lesson.program}
          </Box>
          <Box onClick={onClose} sx={{ cursor: 'pointer', border: `2px solid ${INK}`, borderRadius: '6px', px: 1.25, py: 0.5, fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.8rem', boxShadow: SHADOW_SM, '&:hover': { backgroundColor: '#f0f0f0' } }}>✕ Close</Box>
        </Box>

        {/* Student preview banner */}
        <Box sx={{ backgroundColor: '#2C3E66', py: 0.75, px: 2, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.72rem', color: 'rgba(255,255,255,0.92)' }}>
            <b>Student Preview</b> — this is what a student sees: a scaffolded writing workspace, generated for this lesson.
          </Typography>
        </Box>

        {/* 3-column lesson body */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '0.85fr 1.3fr 0.85fr' }, gap: 1.5, p: { xs: 1.5, md: 2 } }}>
          {/* LEFT — Instructions / Plan / Blocks */}
          <Box sx={{ backgroundColor: '#fff', border: `2px solid ${INK}`, borderRadius: '8px', boxShadow: SHADOW_SM, p: 1.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '0.8rem', color: '#888', flex: 1 }}>Instructions</Typography>
              <SpeakerIcon />
            </Box>
            <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.82rem', color: '#333', lineHeight: 1.5, whiteSpace: 'pre-line', mb: 2 }}>
              {lesson.prompt}
            </Typography>
            <Box sx={{ borderTop: '1px solid #eee', pt: 1.5, mb: 1.5 }}>
              <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.72rem', color: '#999' }}>Writing Plan</Typography>
              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '1rem', color: INK }}>{plan.label}</Typography>
            </Box>
            <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.72rem', color: '#999', mb: 0.75 }}>Blocks</Typography>
            {plan.blocks.map((b) => (
              <Box key={b.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                <Box component="span" sx={{ color: PURPLE, fontWeight: 800, width: 16, textAlign: 'center', fontSize: '0.9rem' }}>{b.icon}</Box>
                <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.82rem', color: '#444' }}>{b.label}</Typography>
              </Box>
            ))}
          </Box>

          {/* CENTER — the document with sentence starters */}
          <Box sx={{ backgroundColor: '#fff', border: `2px solid ${INK}`, borderRadius: '8px', boxShadow: SHADOW_SM, p: { xs: 2, md: 3 }, minHeight: 320 }}>
            <Typography sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 800, fontSize: '1.8rem', color: '#C9CDD4', mb: 2.5 }}>
              Click to write a Title
            </Typography>
            {plan.starters.map((s) => (
              <Typography key={s} sx={{ fontFamily: 'Lato, sans-serif', fontSize: '1.05rem', color: '#B6BBC4', mb: 2.5 }}>{s}</Typography>
            ))}
            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, color: PURPLE, fontSize: '0.95rem' }}>+ Add Block</Typography>
          </Box>

          {/* RIGHT — scaffolds */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {/* Writing Buddy — the AI helper */}
            <Box sx={{ backgroundColor: '#EDE9F7', border: `2px solid ${INK}`, borderRadius: '8px', boxShadow: SHADOW_SM, p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box component="span" sx={{ fontSize: '1.1rem' }}>✨</Box>
                <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '0.85rem', color: PURPLE, flex: 1 }}>Writing Buddy</Typography>
              </Box>
              <Box sx={{ backgroundColor: '#fff', border: '1px solid #D9D2EF', borderRadius: '8px', p: 1 }}>
                <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.78rem', color: '#444', lineHeight: 1.45 }}>
                  Hi! I’m your Writing Buddy. Stuck on what to write next? Ask me and I’ll help you find <i>your</i> words — I’ll never write it for you.
                </Typography>
              </Box>
            </Box>
            <ScaffoldPanel icon="📝" title="Signal Words" defaultOpen>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                {plan.signalWords.map((w) => <WordChip key={w} label={w} bg="#E3F2FD" color="#1565C0" />)}
              </Box>
            </ScaffoldPanel>
            <ScaffoldPanel icon="👍" title="Important Words" defaultOpen>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                {lesson.importantWords.map((w) => <WordChip key={w} label={w} bg="#FBEAD6" color="#9C5A1C" />)}
              </Box>
            </ScaffoldPanel>
          </Box>
        </Box>

        {/* CTA footer */}
        <Box sx={{ backgroundColor: '#fff', borderTop: `2px solid ${INK}`, px: { xs: 2, md: 3 }, py: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '0.95rem', color: INK }}>
              Every student gets these scaffolds, automatically.
            </Typography>
            <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', color: '#666' }}>
              Aligned to {lesson.program} · {lesson.standards.join(', ')}
            </Typography>
            <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.72rem', color: '#9AA0A6', mt: 0.5 }}>
              Source: {lesson.program} — {lesson.theme}
            </Typography>
          </Box>
          <CTAButton dark={false} onClick={onPricing}>See pricing</CTAButton>
          <CTAButton onClick={onRegister}>Try this lesson free →</CTAButton>
        </Box>
      </Box>
    </Box>
  );
}
